<c-container fluid class="p-4">
  <c-row>
    <c-col xs="12">
      <div class="tool-header mb-4">
        <h2 class="mb-1">{{ toolKey() + '.title' | translate }}</h2>
        <p class="text-medium-emphasis mb-0">{{ toolKey() + '.subtitle' | translate }}</p>
      </div>

      @if (error(); as err) {
        <c-alert color="danger" [dismissible]="true" (visibleChange)="clearMessages()">{{ err }}</c-alert>
      }
      @if (notice(); as info) {
        <c-alert color="success" [dismissible]="true" (visibleChange)="clearMessages()">{{ info }}</c-alert>
      }

      <!-- ── Overview ─────────────────────────────────────────────────── -->
      @if (mode() === 'list') {
        <c-card class="border-0">
          <c-card-body>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <p class="text-medium-emphasis mb-0">{{ toolKey() + '.intro' | translate }}</p>
              @if (!listOnly()) {
                <button cButton color="primary" (click)="startNewRequest()">
                  {{ 'health_tools.new_request' | translate }}
                </button>
              }
            </div>

            @if (loading()) {
              <div class="text-center py-5"><c-spinner color="primary" /></div>
            } @else if (tickets().length === 0) {
              <p class="text-medium-emphasis py-4 mb-0">{{ 'health_tools.empty' | translate }}</p>
            } @else {
              <div class="ticket-list">
                @for (item of tickets(); track item.id) {
                  <button type="button" class="ticket-row" (click)="openTicket(item)">
                    <div class="ticket-row__main">
                      <span class="ticket-row__title">{{ item.title }}</span>
                      <span class="ticket-row__meta">
                        #{{ item.number }} · {{ item.updated_at | date: 'dd.MM.yyyy HH:mm' }}
                      </span>
                    </div>
                    <div class="ticket-row__badges">
                      @if (hasNewReply(item)) {
                        <c-badge color="danger">{{ 'health_tools.new_reply' | translate }}</c-badge>
                      }
                      <c-badge [color]="stateColor(item)">{{ item.state }}</c-badge>
                    </div>
                  </button>
                }
              </div>
            }
          </c-card-body>
        </c-card>
      }

      <!-- ── New request ──────────────────────────────────────────────── -->
      @if (mode() === 'new') {
        <c-card class="border-0">
          <c-card-body>
            <label cLabel for="ticketSubject">{{ 'health_tools.subject' | translate }}</label>
            <input
              id="ticketSubject"
              cFormControl
              type="text"
              class="mb-3"
              [ngModel]="subject()"
              (ngModelChange)="subject.set($event)"
              maxlength="200"
            />

            <label cLabel for="ticketMessage">{{ 'health_tools.message' | translate }}</label>
            <textarea
              id="ticketMessage"
              cFormControl
              rows="8"
              class="mb-3"
              [ngModel]="message()"
              (ngModelChange)="message.set($event)"
              maxlength="5000"
            ></textarea>

            <ng-container *ngTemplateOutlet="attachmentPicker"></ng-container>

            <div class="d-flex gap-2 mt-3">
              <button cButton color="primary" [disabled]="busy()" (click)="submitNewRequest()">
                {{ (busy() ? 'health_tools.sending' : 'health_tools.send') | translate }}
              </button>
              <button cButton color="secondary" variant="outline" [disabled]="busy()" (click)="resetToList()">
                {{ 'health_tools.back' | translate }}
              </button>
            </div>
          </c-card-body>
        </c-card>
      }

      <!-- ── Thread ───────────────────────────────────────────────────── -->
      @if (mode() === 'detail') {
        <c-card class="border-0">
          <c-card-body>
            @if (loading()) {
              <div class="text-center py-5"><c-spinner color="primary" /></div>
            } @else if (ticket(); as current) {
              <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
                <div>
                  <h5 class="mb-1">{{ current.title }}</h5>
                  <span class="text-medium-emphasis">
                    {{ 'health_tools.ticket_number' | translate }} #{{ current.number }}
                  </span>
                  <c-badge [color]="stateColor(current)" class="ms-2">{{ current.state }}</c-badge>
                </div>
                <div class="d-flex gap-2">
                  <button cButton color="secondary" variant="outline" (click)="resetToList()">
                    {{ 'health_tools.back' | translate }}
                  </button>
                  <button cButton color="secondary" [disabled]="busy()" (click)="toggleState()">
                    {{ (isClosed(current) ? 'health_tools.reopen_ticket' : 'health_tools.close_ticket') | translate }}
                  </button>
                </div>
              </div>

              <div class="thread mb-4">
                @for (article of articles(); track article.id) {
                  <div class="thread__item" [class.thread__item--mine]="article.from_customer">
                    <div class="thread__meta">
                      <strong>
                        {{ (article.from_customer ? 'health_tools.you' : 'health_tools.support_team') | translate }}
                      </strong>
                      <span>{{ article.created_at | date: 'dd.MM.yyyy HH:mm' }}</span>
                    </div>
                    @if (isHtml(article)) {
                      <div class="thread__body" [innerHTML]="article.body"></div>
                    } @else {
                      <div class="thread__body thread__body--plain">{{ article.body }}</div>
                    }
                    @if (article.attachments.length) {
                      <div class="thread__attachments">
                        @for (attachment of article.attachments; track attachment.id) {
                          <button type="button" class="attachment-chip" (click)="downloadAttachment(article, attachment)">
                            {{ attachment.filename }}
                            @if (formatSize(attachment.size); as size) {
                              <span class="attachment-chip__size">{{ size }}</span>
                            }
                          </button>
                        }
                      </div>
                    }
                  </div>
                }
              </div>

              @if (isClosed(current)) {
                <p class="text-medium-emphasis mb-0">{{ 'health_tools.closed_hint' | translate }}</p>
              } @else {
                <label cLabel for="replyMessage">{{ 'health_tools.reply' | translate }}</label>
                <textarea
                  id="replyMessage"
                  cFormControl
                  rows="5"
                  class="mb-3"
                  [ngModel]="message()"
                  (ngModelChange)="message.set($event)"
                  [placeholder]="'health_tools.reply_placeholder' | translate"
                  maxlength="5000"
                ></textarea>

                <ng-container *ngTemplateOutlet="attachmentPicker"></ng-container>

                <button cButton color="primary" class="mt-3" [disabled]="busy()" (click)="submitReply()">
                  {{ (busy() ? 'health_tools.sending' : 'health_tools.send_reply') | translate }}
                </button>
              }
            }
          </c-card-body>
        </c-card>
      }
    </c-col>
  </c-row>
</c-container>

<!-- Shared by the new-request form and the reply box. -->
<ng-template #attachmentPicker>
  <div class="attachments">
    <label class="attachment-add" cButton color="secondary" variant="outline">
      {{ 'health_tools.add_files' | translate }}
      <input type="file" multiple hidden (change)="onFilesSelected($event)" />
    </label>
    <span class="text-medium-emphasis small ms-2">{{ 'health_tools.attachment_hint' | translate }}</span>

    @if (attachments().length) {
      <div class="attachments__list mt-2">
        @for (attachment of attachments(); track attachment.filename + $index) {
          <span class="attachment-chip">
            {{ attachment.filename }}
            <button type="button" class="attachment-chip__remove" (click)="removeAttachment($index)">&times;</button>
          </span>
        }
      </div>
    }
  </div>
</ng-template>
